import { useState, useRef, useEffect } from 'react'
import { Button } from '../../ui/button'
import useFileUploader from 'hooks/useFileUploader'
import { ArrowRight } from 'lucide-react'
import type { DealFormData, UrlItem } from '../../../lib/types'
import { validateDealOverview } from '../../../lib/step-validators'
import {
  OverviewBasicsSection,
  OverviewBannerSection,
  OverviewLogoSection,
  OverviewUrlsSection,
  VisibilityEligibilitySection,
} from './overview'

interface StepDealOverviewProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate?: (data: Partial<DealFormData>) => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

export function StepDealOverview({
  formData,
  onComplete,
  onUpdate,
  headingId,
  onValidationError,
  onValidationErrors,
  requestChanges,
}: StepDealOverviewProps) {
  const { uploadFile } = useFileUploader()
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoUploadError, setLogoUploadError] = useState<string | undefined>()
  const [bannerUploading, setBannerUploading] = useState(false)
  const [bannerUploadError, setBannerUploadError] = useState<
    string | undefined
  >()
  const handleLogoSelected = (file: File | null) => {
    updateField('logo', file)
    if (!file) {
      onUpdate && onUpdate({ logo: null, logoUrl: '' } as any)
      setLogoUploading(false)
      setLogoUploadError(undefined)
      setLocalData((prev) => ({ ...prev, logo: null, logoUrl: '' }))
      return
    }
    setLogoUploading(true)
    setLogoUploadError(undefined)
    uploadFile(
      { file, path: `submission/logo-${Date.now()}-${file.name}` },
      (res: { url: string }) => {
        onUpdate && onUpdate({ logoUrl: res.url } as any)
        setLogoUploading(false)
        setLogoUploadError(undefined)
        setLocalData((prev) => ({ ...prev, logoUrl: res.url }))
      },
      () => {
        setLogoUploading(false)
        setLogoUploadError('Upload failed. Please try again.')
      },
    )
  }
  const handleBannerSelected = (file: File | null) => {
    updateField('banner', file)
    if (!file) {
      onUpdate?.({ banner: null, bannerUrl: '' })
      setBannerUploading(false)
      setBannerUploadError(undefined)
      setLocalData((previous) => ({
        ...previous,
        banner: null,
        bannerUrl: '',
      }))
      return
    }

    setBannerUploading(true)
    setBannerUploadError(undefined)
    uploadFile(
      { file, path: `submission/banner-${Date.now()}-${file.name}` },
      (response: { url: string }) => {
        onUpdate?.({ bannerUrl: response.url })
        setBannerUploading(false)
        setBannerUploadError(undefined)
        setLocalData((previous) => ({
          ...previous,
          bannerUrl: response.url,
        }))
      },
      () => {
        setBannerUploading(false)
        setBannerUploadError('Upload failed. Please try again.')
      },
    )
  }
  const [localData, setLocalData] = useState({
    projectName: formData.projectName || '',
    logo: formData.logo || null,
    logoUrl: formData.logoUrl || '',
    banner: formData.banner || null,
    bannerUrl: formData.bannerUrl || '',
    category: formData.category || [],
    urls:
      (formData.urls || []).filter(
        (url) => url.type !== 'discord' || (url.url || '').trim() !== '',
      ).length > 0
        ? formData.urls!.filter(
            (url) => url.type !== 'discord' || (url.url || '').trim() !== '',
          )
        : [
            {
              id: 'website',
              type: 'website' as const,
              url: formData.website || '',
            },
            { id: 'x', type: 'x' as const, url: formData.twitter || '' },
          ],
    researchReport: formData.researchReport || '',
    researchNotes: formData.researchNotes || '',
    requestResearch: formData.requestResearch || false,
    researchPath: formData.researchPath || 'link',
    requestResearchSupport: formData.requestResearchSupport || false,
    projectMemo: formData.projectMemo || '',
    jurisdiction: formData.jurisdiction || '',
    jurisdictionOther: (formData as any).jurisdictionOther || '',
    countryRestrictions: formData.countryRestrictions || [],
    researchMaterials: formData.researchMaterials || 'link',
    salesType: (formData as any).salesType || '',
    salesTypeDetail:
      (formData as any).salesTypeDetail ||
      ((formData as any).salesType === 'Public Sale' ? 'IDO' : ''),
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isCountryRestrictionsOpen, setIsCountryRestrictionsOpen] =
    useState(false)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const countryRestrictionsDropdownRef = useRef<HTMLDivElement>(null)

  const addUrlRow = () => {
    const newId = `url-${Date.now()}`
    const newUrl: UrlItem = { id: newId, type: 'custom', url: '' }
    setLocalData((prev) => {
      const newData = { ...prev, urls: [...prev.urls, newUrl] }
      onUpdate?.({ urls: newData.urls })
      return newData
    })
  }

  const removeUrlRow = (id: string) => {
    setLocalData((prev) => {
      const newData = {
        ...prev,
        urls: prev.urls.filter((url) => url.id !== id),
      }
      onUpdate?.({ urls: newData.urls })
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[`url-${id}`]
        return newErrors
      })
      return newData
    })
  }

  const updateUrlRow = (id: string, field: keyof UrlItem, value: string) => {
    setLocalData((prev) => {
      const newData = {
        ...prev,
        urls: prev.urls.map((url) =>
          url.id === id ? { ...url, [field]: value } : url,
        ),
      }
      onUpdate?.({ urls: newData.urls })
      return newData
    })
    if (field === 'url') {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[`url-${id}`]
        return newErrors
      })
    }
  }

  const updateField = (field: string, value: any) => {
    if (field === 'researchPath') {
      setLocalData((prev) => ({
        ...prev,
        [field]: value,
        requestResearchSupport: value === 'outsourced',
        researchReport: value === 'outsourced' ? '' : prev.researchReport,
      }))
      if (errors.researchReport)
        setErrors((prev) => ({ ...prev, researchReport: '' }))
      return
    }
    if (field === 'salesType') {
      setLocalData((prev) => ({ ...prev, salesType: value }))
      setErrors((prev) => ({
        ...prev,
        salesType: '',
        salesTypeDetail: '',
        researchReport: '',
      }))
      onUpdate && onUpdate({ [field]: value })
      return
    }
    setLocalData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }))
    onUpdate && onUpdate({ [field]: value })
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryRestrictionsDropdownRef.current &&
        !countryRestrictionsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryRestrictionsOpen(false)
      }
      if (
        openDropdownId &&
        !document.getElementById(openDropdownId)?.contains(event.target as Node)
      ) {
        setOpenDropdownId(null)
      }
    }
    if (isCountryRestrictionsOpen || openDropdownId)
      document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCountryRestrictionsOpen, openDropdownId])

  const handleSubmit = () => {
    const { errors: newErrors, isValid } = validateDealOverview(localData)
    setErrors(newErrors)
    if (!isValid) {
      const fieldLabels: Record<string, string> = {
        projectName: 'Project Name',
        salesType: 'Type of Deal',
        salesTypeDetail: 'Deal Type Detail',
        jurisdiction: 'Jurisdiction',
        jurisdictionOther: 'Specify Jurisdiction',
        logo: 'Logo',
        pitchDeck: 'Pitch Deck',
        researchReport: 'Research Materials',
        'url-website': 'Official Site URL',
        'url-x': 'X (Twitter) URL',
      }

      const mappedErrors = Object.keys(newErrors).map((id) => ({
        id,
        label: fieldLabels[id] || id,
      }))

      if (onValidationErrors) {
        onValidationErrors(mappedErrors)
      } else {
        onValidationError?.(
          'Please complete all required fields before continuing.',
        )
      }
    } else {
      onValidationErrors?.([])
      onValidationError?.('')
      onComplete && onComplete(localData as any)
    }
  }

  useEffect(() => {
    setLocalData({
      projectName: formData.projectName || '',
      logo: formData.logo || null,
      logoUrl: formData.logoUrl || '',
      banner: formData.banner || null,
      bannerUrl: formData.bannerUrl || '',
      category: formData.category || [],
      urls:
        (formData.urls || []).filter(
          (url) => url.type !== 'discord' || (url.url || '').trim() !== '',
        ).length > 0
          ? formData.urls!.filter(
              (url) => url.type !== 'discord' || (url.url || '').trim() !== '',
            )
          : [
              {
                id: 'website',
                type: 'website' as const,
                url: formData.website || '',
              },
              { id: 'x', type: 'x' as const, url: formData.twitter || '' },
            ],
      researchReport: formData.researchReport || '',
      researchNotes: formData.researchNotes || '',
      requestResearch: formData.requestResearch || false,
      researchPath: formData.researchPath || 'link',
      requestResearchSupport: formData.requestResearchSupport || false,
      projectMemo: formData.projectMemo || '',
      jurisdiction: formData.jurisdiction || '',
      jurisdictionOther: (formData as any).jurisdictionOther || '',
      countryRestrictions: formData.countryRestrictions || [],
      researchMaterials: formData.researchMaterials || 'link',
      salesType: (formData as any).salesType || '',
      salesTypeDetail: (formData as any).salesTypeDetail || '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-6">
      <VisibilityEligibilitySection
        localData={localData as any}
        errors={errors}
        updateField={updateField}
        requestChanges={requestChanges}
      />

      <OverviewBasicsSection
        headingId={headingId}
        localData={localData as any}
        errors={errors}
        openDropdownId={openDropdownId}
        setOpenDropdownId={setOpenDropdownId}
        updateField={updateField}
        requestChanges={requestChanges}
      />

      <OverviewLogoSection
        localData={localData as any}
        errors={errors}
        logoUploading={logoUploading}
        logoUploadError={logoUploadError}
        onLogoSelected={handleLogoSelected}
        requestChanges={requestChanges}
      />

      <OverviewBannerSection
        localData={localData}
        bannerUploading={bannerUploading}
        bannerUploadError={bannerUploadError}
        onBannerSelected={handleBannerSelected}
        requestChanges={requestChanges}
      />

      <OverviewUrlsSection
        localData={localData as any}
        errors={errors}
        openDropdownId={openDropdownId}
        setOpenDropdownId={setOpenDropdownId}
        updateField={updateField}
        updateUrlRow={updateUrlRow}
        removeUrlRow={removeUrlRow}
        addUrlRow={addUrlRow}
        setErrors={setErrors}
        requestChanges={requestChanges}
      />

      <div className="flex justify-end pt-6">
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
